import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { Card } from 'primereact/card';
import { Badge } from 'primereact/badge';
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import api from '../../services/api';
import './AdminUsersManager.css';

const emptyForm = {
  username: '',
  name: '',
  email: '',
  password: '',
  pin: '',
};

function randomPin() {
  return String(Math.floor(100000 + Math.random() * 900000));
}

function syncStoredUser(updated) {
  try {
    const stored = JSON.parse(localStorage.getItem('user') || 'null');
    if (!stored) return;
    const sameId = stored.id && updated.id && stored.id === updated.id;
    const sameUsername = stored.username && stored.username === updated.username;
    if (!sameId && !sameUsername) return;
    localStorage.setItem(
      'user',
      JSON.stringify({
        ...stored,
        id: updated.id || stored.id,
        username: updated.username,
        name: updated.name,
        email: updated.email,
      })
    );
  } catch {
    // ignore storage issues
  }
}

export default function AdminUsersManager() {
  const toast = useRef(null);
  const [items, setItems] = useState([]);
  const [me, setMe] = useState('');
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [pinDialog, setPinDialog] = useState({ visible: false, pin: '', name: '' });
  const [pinLoadingId, setPinLoadingId] = useState('');

  const fetchUsers = useCallback(async (query = q) => {
    try {
      setLoading(true);
      const { data } = await api.get('/admin_users', { params: { q: query } });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setMe(data.me || '');
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err?.response?.data?.message || 'Failed to load admin users',
      });
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => {
    fetchUsers('');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      username: row.username || '',
      name: row.name || '',
      email: row.email || '',
      password: '',
      pin: '',
    });
    setDialogOpen(true);
  };

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const save = async () => {
    if (!form.username.trim()) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Missing username',
        detail: 'Username is required',
      });
      return;
    }
    if (!editing && form.password.trim().length < 6) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Password required',
        detail: 'New accounts need a password of at least 6 characters',
      });
      return;
    }
    if (form.password && form.password.length < 6) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Password too short',
        detail: 'Password must be at least 6 characters',
      });
      return;
    }
    if (form.pin && !/^\d{6}$/.test(form.pin)) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Invalid PIN',
        detail: 'PIN must be exactly 6 digits',
      });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        username: form.username.trim(),
        name: form.name.trim(),
        email: form.email.trim(),
      };
      if (form.password) payload.password = form.password;
      if (form.pin) payload.pin = form.pin;

      const { data } = editing
        ? await api.put(`/admin_users/${editing.id}`, payload)
        : await api.post('/admin_users', payload);

      syncStoredUser(data);
      setDialogOpen(false);
      await fetchUsers();
      toast.current?.show({
        severity: 'success',
        summary: 'Saved',
        detail: editing ? 'Admin user updated' : 'Admin user created',
      });
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err?.response?.data?.message || 'Failed to save admin user',
      });
    } finally {
      setSaving(false);
    }
  };

  const viewPin = async (row) => {
    if (!row.hasPin) {
      toast.current?.show({
        severity: 'info',
        summary: 'No PIN',
        detail: 'This account does not have a PIN yet',
      });
      return;
    }
    setPinLoadingId(row.id);
    try {
      const { data } = await api.get(`/admin_users/${row.id}/pin`);
      setPinDialog({
        visible: true,
        pin: data.pin || '',
        name: row.name || row.username,
      });
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err?.response?.data?.message || 'Could not load PIN',
      });
    } finally {
      setPinLoadingId('');
    }
  };

  const copyPin = async () => {
    try {
      await navigator.clipboard.writeText(pinDialog.pin);
      toast.current?.show({
        severity: 'success',
        summary: 'Copied',
        detail: 'PIN copied to clipboard',
      });
    } catch {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Could not copy PIN',
      });
    }
  };

  const remove = (row) => {
    confirmDialog({
      message: `Delete admin user “${row.name || row.username}”?`,
      header: 'Delete admin user',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await api.delete(`/admin_users/${row.id}`);
          await fetchUsers();
          toast.current?.show({
            severity: 'success',
            summary: 'Deleted',
            detail: 'Admin user removed',
          });
        } catch (err) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: err?.response?.data?.message || 'Failed to delete',
          });
        }
      },
    });
  };

  return (
    <Card className="admin-users-page">
      <div className="card">
        <Toast ref={toast} />
        <ConfirmDialog />

        <div className="grid mb-4">
          <div className="col-12 md:col-7 flex align-items-center">
            <h2 className="m-0 main-text font-bold vertical-align-middle">
              <span>Admin users</span>
              <Badge
                className="ml-2 b-4 relative"
                value={total ?? 0}
                style={{ bottom: '3px', backgroundColor: '#2020A0' }}
              />
            </h2>
          </div>
          <div className="col-12 md:col-5 flex justify-content-end">
            <Button
              label="New admin"
              icon="pi pi-plus"
              className="p-button-primary p-button-sm secondary-button"
              onClick={openCreate}
            />
          </div>
        </div>

        <p className="admin-page-hint mb-4">
          Panel accounts only — not mobile users. Edit name, email, password and the 6-digit PIN used by the hidden mobile unlock.
        </p>

        <div className="grid mb-4">
          <div className="col-12 flex gap-2 align-items-end flex-wrap">
            <div className="flex flex-column" style={{ minWidth: 250 }}>
              <label style={{ fontSize: '0.95rem' }}>
                <strong>Search</strong>
              </label>
              <InputText
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchUsers()}
                className="w-full p-button-sm"
                placeholder="Search name / username / email"
              />
            </div>
            <Button
              label="Search"
              icon="pi pi-search"
              className="p-button-primary p-button-sm secondary-button"
              onClick={() => fetchUsers()}
            />
            <Button
              label="Clear"
              icon="pi pi-times"
              className="p-button-text text-danger p-button-sm"
              onClick={() => {
                setQ('');
                fetchUsers('');
              }}
            />
          </div>
        </div>

        {loading && !items.length ? (
          <div
            className="flex flex-column align-items-center justify-content-center"
            style={{ minHeight: '300px' }}
          >
            <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', color: '#2020A0' }} />
            <div style={{ marginTop: '1rem', fontSize: '1.2rem', color: '#555' }}>
              Loading admin users...
            </div>
          </div>
        ) : (
          <DataTable
            value={items}
            paginator
            rows={25}
            loading={loading}
            emptyMessage="No admin users found."
            className="p-datatable-sm"
            showGridlines
            stripedRows
          >
            <Column
              field="name"
              header="Name"
              sortable
              body={(row) => (
                <span>
                  {row.name || '—'}
                  {row.username === me ? <span className="admin-users-you">You</span> : null}
                </span>
              )}
            />
            <Column field="username" header="Username" sortable />
            <Column
              field="email"
              header="Email"
              sortable
              body={(row) => row.email || <span className="text-500">—</span>}
            />
            <Column
              header="PIN"
              body={(row) =>
                row.hasPin ? (
                  <Tag value="Assigned" severity="success" />
                ) : (
                  <Tag value="Missing" severity="warning" />
                )
              }
              style={{ width: '8rem' }}
            />
            <Column
              header=""
              body={(row) => (
                <div className="flex gap-1 justify-content-end">
                  <Button
                    icon="pi pi-eye"
                    className="p-button-text p-button-sm"
                    loading={pinLoadingId === row.id}
                    disabled={!row.hasPin}
                    onClick={() => viewPin(row)}
                    tooltip="View PIN"
                    tooltipOptions={{ position: 'top' }}
                  />
                  <Button
                    icon="pi pi-pencil"
                    className="p-button-text p-button-sm"
                    onClick={() => openEdit(row)}
                    tooltip="Edit"
                    tooltipOptions={{ position: 'top' }}
                  />
                  <Button
                    icon="pi pi-trash"
                    className="p-button-text p-button-sm p-button-danger"
                    disabled={row.username === me}
                    onClick={() => remove(row)}
                    tooltip={row.username === me ? 'You cannot delete yourself' : 'Delete'}
                    tooltipOptions={{ position: 'top' }}
                  />
                </div>
              )}
              style={{ width: '10rem' }}
            />
          </DataTable>
        )}
      </div>

      <Dialog
        header={editing ? 'Edit admin user' : 'New admin user'}
        visible={dialogOpen}
        style={{ width: 'min(560px, 94vw)' }}
        onHide={() => setDialogOpen(false)}
        footer={
          <div className="flex justify-content-end gap-2">
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-text"
              onClick={() => setDialogOpen(false)}
            />
            <Button
              label={saving ? 'Saving...' : 'Save'}
              icon="pi pi-check"
              className="p-button-primary primary-button"
              loading={saving}
              onClick={save}
            />
          </div>
        }
      >
        <div className="admin-users-form-grid">
          <div className="admin-users-field">
            <label htmlFor="admin-username">Username</label>
            <InputText
              id="admin-username"
              value={form.username}
              onChange={(e) => setField('username', e.target.value)}
              className="w-full"
              autoComplete="off"
            />
          </div>
          <div className="admin-users-field">
            <label htmlFor="admin-name">Name</label>
            <InputText
              id="admin-name"
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              className="w-full"
            />
          </div>
          <div className="admin-users-field span-2">
            <label htmlFor="admin-email">Email</label>
            <InputText
              id="admin-email"
              type="email"
              value={form.email}
              onChange={(e) => setField('email', e.target.value)}
              className="w-full"
              placeholder="you@bordergarita.com"
            />
          </div>
          <div className="admin-users-field span-2">
            <label htmlFor="admin-password">
              {editing ? 'New password' : 'Password'}
            </label>
            <Password
              id="admin-password"
              value={form.password}
              onChange={(e) => setField('password', e.target.value)}
              className="w-full"
              inputClassName="w-full"
              toggleMask
              feedback={false}
              autoComplete="new-password"
            />
            <small>
              {editing
                ? 'Leave empty to keep the current password.'
                : 'At least 6 characters.'}
            </small>
          </div>
          <div className="admin-users-field span-2">
            <label htmlFor="admin-pin">6-digit PIN</label>
            <div className="admin-users-pin-row">
              <InputText
                id="admin-pin"
                value={form.pin}
                onChange={(e) =>
                  setField('pin', e.target.value.replace(/\D/g, '').slice(0, 6))
                }
                className="w-full"
                inputMode="numeric"
                maxLength={6}
                placeholder={editing?.hasPin ? '••••••  leave empty to keep' : '123456'}
              />
              <Button
                type="button"
                label="Generate"
                icon="pi pi-refresh"
                className="p-button-outlined"
                onClick={() => setField('pin', randomPin())}
              />
            </div>
            <small>Used by the hidden mobile unlock. Leave empty to keep the current PIN.</small>
            {editing?.hasPin ? (
              <div className="mt-2">
                <Button
                  type="button"
                  label="Remove PIN"
                  icon="pi pi-times"
                  className="p-button-text p-button-sm p-button-danger"
                  onClick={async () => {
                    try {
                      setSaving(true);
                      await api.put(`/admin_users/${editing.id}`, { clearPin: true });
                      setDialogOpen(false);
                      await fetchUsers();
                      toast.current?.show({
                        severity: 'success',
                        summary: 'PIN removed',
                        detail: 'This account no longer has a PIN',
                      });
                    } catch (err) {
                      toast.current?.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: err?.response?.data?.message || 'Could not remove PIN',
                      });
                    } finally {
                      setSaving(false);
                    }
                  }}
                />
              </div>
            ) : null}
          </div>
        </div>
      </Dialog>

      <Dialog
        className="admin-users-pin-dialog"
        header="View PIN"
        visible={pinDialog.visible}
        style={{ width: 'min(420px, 94vw)' }}
        onHide={() => setPinDialog({ visible: false, pin: '', name: '' })}
        dismissableMask
      >
        <div className="admin-users-pin-vault">
          <div className="admin-users-pin-vault-icon">
            <i className="pi pi-lock" />
          </div>
          <h3>{pinDialog.name || 'Admin PIN'}</h3>
          <p>Keep this code private. It unlocks the hidden mobile mode.</p>
          <div className="admin-users-pin-digits">
            {(pinDialog.pin || '------').split('').map((digit, idx) => (
              <div key={`${digit}-${idx}`} className="admin-users-pin-digit">
                {digit}
              </div>
            ))}
          </div>
          <Button
            label="Copy PIN"
            icon="pi pi-copy"
            className="p-button-primary p-button-sm secondary-button"
            onClick={copyPin}
          />
        </div>
      </Dialog>
    </Card>
  );
}
